/*----------------------------------
- DOMAINS MENU CHIP
----------------------------------*/

import type { LandingHeaderNavPanelItem } from '@/client/catalogs/landing/pageContent';
import { readMenuItemCount } from './menuShared';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainsMenuChipProps = {
    item: LandingHeaderNavPanelItem;
    fallbackHref: string;
    /** TLD chips split the leading dot from the extension so it can be dimmed. */
    isTld?: boolean;
    onClick?: () => void;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const CHIP_CLASS = 'min-h-[38px] gap-[7px] border border-[#e6e2da] bg-white px-[13px] text-[13.5px]';

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * One ending or one sector in the phone menu. Desktop uses rows instead since the
 * 2026-09-17 redesign, so this is the phone's shape alone: a wrapping chip is the only
 * way ten short labels fit a 402px column without ten full-width rows of whitespace.
 */
export default function DomainsMenuChip({ item, fallbackHref, isTld = false, onClick }: DomainsMenuChipProps) {
    const href = item.href || fallbackHref;
    const count = readMenuItemCount(item);
    const label = isTld ? item.label.replace(/^\./, '') : item.label;

    return (
        <a
            href={href}
            onClick={onClick}
            aria-label={isTld ? `Browse .${label} domains` : undefined}
            class={`inline-flex items-center rounded-full font-semibold text-[#1b1d1e] transition-colors ${CHIP_CLASS}`}
        >
            <span>
                {isTld ? <span class="text-[#66696b]">.</span> : null}
                {label}
            </span>
            {count ? <span class="text-[11.5px] tabular-nums text-[#66696b]">{count}</span> : null}
        </a>
    );
}
